import React, { useRef, useMemo, CSSProperties } from "react";
import Image from "next/image";
import { languageIds } from "@/lib/language";
import { ImageGroup } from "@/types/layerslider";
import { getTransform } from "@/main/_components/utils/slider-animations";

interface AnimatedImageProps {
  group: ImageGroup;
  value: string;
  lang: string;
}
const defaultDimensions = { width: 900, height: 600 };
const isInView = true;
const isLoaded = true;

const AnimatedImage: React.FC<AnimatedImageProps> = ({ group, value, lang }) => {
  const imageRef = useRef<HTMLImageElement | null>(null);

  const getEasing = (easing: string) => {
    if (easing === "easeOutCirc") return "cubic-bezier(0, 0.55, 0.45, 1)";
    if (easing === "easeOutSine") return "cubic-bezier(0.61, 1, 0.88, 1)";
    if (easing === "easeInOutExpo") return "cubic-bezier(0.87, 0, 0.13, 1)";
    return "linear";
  };

  const easingIn = getEasing(group.easingin);
  const easingOut = getEasing(group.easingout);
  const transitionInTransform = useMemo(
    () => getTransform(group.transitionin, easingIn),
    [group.transitionin, easingIn],
  );
  const transitionOutTransform = useMemo(
    () => getTransform(group.transitionout, easingOut),
    [group.transitionout, easingOut],
  );

  // Compute inline style without dynamic dimensions
  const imageStyle: CSSProperties = useMemo(() => {
    // we will need these, do not delete them
    const leftPos =
      group.left[languageIds[lang]]?.trim() === "" ? 0 : group.left[languageIds[lang]];
    // const topPos = group.top[languageIds[lang]]?.trim() === "" ? 0 : group.top[languageIds[lang]];

    let transitionStr = "none";
    if (isLoaded) {
      if (isInView) {
        transitionStr =
          group.transitionin === "fade()" || group.transitionin === "bottom(short)"
            ? `opacity ${group.durationin}ms ${easingIn}`
            : `opacity ${group.durationin}ms ${easingIn}, transform 500ms ${easingIn}`;
      } else {
        transitionStr =
          group.transitionout === "fade()" || group.transitionout === "bottom(short)"
            ? `opacity ${group.durationout}ms ${easingOut}`
            : `opacity ${group.durationout}ms ${easingOut}, transform 500ms ${easingOut}`;
      }
    }

    const transformStyle = isInView
      ? group.transitionin === "fade()" || group.transitionin === "bottom(short)"
        ? ""
        : transitionInTransform
      : group.transitionout === "fade()" || group.transitionout === "bottom(short)"
        ? ""
        : transitionOutTransform;

    const animationStyle =
      isInView && group.transitionin !== "bottom(short)"
        ? `${transitionInTransform} ${group.durationin}ms ${easingIn} forwards`
        : !isInView && group.transitionout !== "bottom(short)"
          ? `${transitionOutTransform} ${group.durationout}ms ${easingOut} forwards`
          : group.transitionin === "bottom(short)" || group.transitionout === "bottom(short)"
            ? `bottomShort ${group.durationin}ms ${easingIn} forwards`
            : "none";

    return {
      position: "absolute",
      left: `${leftPos}px`,
      // top: `${topPos}px`, we will need these, do not delete them
      top: 0,
      minHeight: `${group.minheight || 0}px`,
      opacity: isLoaded && isInView ? 1 : 0,
      transition: transitionStr,
      transform: transformStyle,
      animation: animationStyle,
      zIndex: `calc(${group.p_index} + 5px)`,
      order: group.sort_order,
      objectFit: "scale-down",
      maxWidth: "100%",
      height: "auto",
    };
  }, [group, lang, easingIn, easingOut, transitionInTransform, transitionOutTransform]);

  return (
    <Image
      fetchPriority="high"
      ref={imageRef}
      src={`${process.env.NEXT_PUBLIC_BASE_URL}/image/${value}`}
      alt={`Image ${lang}`}
      width={defaultDimensions.width}
      height={defaultDimensions.height}
      style={imageStyle}
      priority
      sizes="100vw"
    />
  );
};

export default AnimatedImage;
